import { forwardRef, useImperativeHandle, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
} from '@mui/material';
import { UserPaymentMethod, PaymentMethod } from '@finpay-payment-v3/payment-types';

import { createSetupIntent } from '../services/payment-service';
import PaymentHubCardForm, { CardPaymentRef } from './payment-hub-card-form';
import PaymentHubACHForm, { ACHPaymentRef } from './payment-hub-ach-form';
import PaymentMethodList from './payment-method-list';
import PaymentSourceTabs, { RECURRING_PAYMENT_OPTIONS, PaymentSourceType } from './payment-source-tabs';
import { AddressDetails } from './guarantor-address-fields';

export interface RecurringPaymentMethodSectionProps {
  savedPaymentMethods: UserPaymentMethod[];
  billingDetails: {
    firstName: string;
    lastName: string;
    email: string;
  };
  onBillingDetailsChange: (field: string, value: string) => void;
  originalPayorEmail?: string;
  isAddingNewChampion?: boolean;
  showAddressFields?: boolean;
  addressDetails?: AddressDetails;
  onAddressDetailsChange?: (field: keyof AddressDetails, value: string) => void;
}

export interface RecurringPaymentMethodSectionRef {
  getSelectedMethodId: () => number | null;
  isAddingNew: () => boolean;
  getPaymentSource: () => 'CARD' | 'ACH';
  getCardFormRef: () => CardPaymentRef | null;
  getAchFormRef: () => ACHPaymentRef | null;
  getSetupIntentId: () => string | null;
  reset: () => void;
}

const RecurringPaymentMethodSection = forwardRef<RecurringPaymentMethodSectionRef, RecurringPaymentMethodSectionProps>(
  ({ savedPaymentMethods, billingDetails, onBillingDetailsChange, originalPayorEmail, isAddingNewChampion = false, showAddressFields = false, addressDetails, onAddressDetailsChange }, ref) => {
    const [paymentSource, setPaymentSource] = useState<'CARD' | 'ACH'>('CARD');
    const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
    const [isAddingNewMethod, setIsAddingNewMethod] = useState(false);
    const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<string | null>(null);
    const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
    const [isSetupIntentLoading, setIsSetupIntentLoading] = useState(false);
    const cardFormRef = useRef<CardPaymentRef>(null);
    const achFormRef = useRef<ACHPaymentRef>(null);
    
    useImperativeHandle(ref, () => ({
      getSelectedMethodId: () => isAddingNewMethod ? null : selectedMethodId,
      isAddingNew: () => isAddingNewMethod,
      getPaymentSource: () => paymentSource,
      getCardFormRef: () => cardFormRef.current,
      getAchFormRef: () => achFormRef.current,
      getSetupIntentId: () => setupIntentId,
      reset: () => {
        setSelectedMethodId(null);
        setIsAddingNewMethod(false);
        setSetupIntentClientSecret(null);
        setSetupIntentId(null);
      },
    }));
    
    const handlePaymentSourceChange = (newSource: 'CARD' | 'ACH') => {
      setPaymentSource(newSource);
      setSelectedMethodId(null);
      setIsAddingNewMethod(false);
      setSetupIntentClientSecret(null);
    };
    
    const handleSelectMethod = (methodId: number) => {
      setSelectedMethodId(methodId);
      setIsAddingNewMethod(false);
    };
    
    const handleAddNew = async () => {
      // Prevent duplicate calls if already loading or already adding
      if (isSetupIntentLoading || isAddingNewMethod) {
        return;
      }
      
      setIsAddingNewMethod(true);
      setSelectedMethodId(null);
      setIsSetupIntentLoading(true);
      setSetupIntentClientSecret(null);
      
      const paymentMethodTypeId = paymentSource === 'ACH' ? PaymentMethod.BankAccount : PaymentMethod.Card;
      const result = await createSetupIntent({ paymentMethodTypeId });
      
      if (result.success && result.clientSecret && result.setupIntentId) {
        setSetupIntentClientSecret(result.clientSecret);
        setSetupIntentId(result.setupIntentId);
      }
      
      setIsSetupIntentLoading(false);
    };
    
    const listSelectedMethodId = isAddingNewMethod ? 'new' : (selectedMethodId?.toString() ?? null);
    
    const handleListMethodSelect = (methodIdStr: string) => {
      if (methodIdStr === 'new') {
        handleAddNew();
      } else {
        handleSelectMethod(parseInt(methodIdStr, 10));
      }
    };
    
    return (
      <>
        <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }} test-id="recurring-payment-method-section">
          <Typography variant="subtitle1" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Recurring Payment Method
          </Typography>
        </Grid>
        <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', py: 0.5 }}>
          <Box display="flex" justifyContent="flex-end" sx={{ width: '100%' }}>
            <Box sx={{ width: 'auto', minWidth: 200 }} test-id="recurring-payment-source-tabs">
              <PaymentSourceTabs
                value={paymentSource}
                onChange={(newSource) => handlePaymentSourceChange(newSource as 'CARD' | 'ACH')}
                options={RECURRING_PAYMENT_OPTIONS}
                name="recurringPaymentSource"
                minWidth={200}
              />
            </Box>
          </Box>
        </Grid>
        
        <Grid item xs={12}>
          <PaymentMethodList
            savedPaymentMethods={savedPaymentMethods}
            paymentSource={paymentSource}
            selectedMethodId={listSelectedMethodId}
            onMethodSelect={handleListMethodSelect}
            showDefaultChip={false}
          />

          {isAddingNewMethod && paymentSource === 'CARD' && (
            <Box sx={{ mt: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 2, backgroundColor: '#fafafa' }}>
              {isSetupIntentLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    Loading payment form...
                  </Typography>
                </Box>
              ) : (
                <PaymentHubCardForm
                  key={`recurring-card-form-${setupIntentClientSecret}`}
                  ref={cardFormRef}
                  isActive={isAddingNewMethod && paymentSource === 'CARD'}
                  clientSecret={setupIntentClientSecret}
                  isLoading={isSetupIntentLoading}
                  billingDetails={billingDetails}
                  onBillingDetailsChange={onBillingDetailsChange}
                  showNameFields={isAddingNewChampion}
                  originalEmail={originalPayorEmail}
                  showAddressFields={showAddressFields}
                  addressDetails={addressDetails}
                  onAddressDetailsChange={onAddressDetailsChange}
                />
              )}
            </Box>
          )}

          {isAddingNewMethod && paymentSource === 'ACH' && (
            <Box sx={{ mt: 2 }} test-id="recurring-ach-form-container">
              <PaymentHubACHForm
                ref={achFormRef}
                isActive={isAddingNewMethod && paymentSource === 'ACH'}
                billingDetails={billingDetails}
                onBillingDetailsChange={onBillingDetailsChange}
                showNameFields={isAddingNewChampion}
                originalEmail={originalPayorEmail}
                savedBankAccounts={[]}
                showAddressFields={showAddressFields}
                addressDetails={addressDetails}
                onAddressDetailsChange={onAddressDetailsChange}
              />
            </Box>
          )}
        </Grid>
      </>
    );
  }
);

RecurringPaymentMethodSection.displayName = 'RecurringPaymentMethodSection';

export default RecurringPaymentMethodSection;
